import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../App';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';
import { logoDarkmode, logoLightmode } from '../assets';

const Layout = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHover, setSidebarHover] = useState(false);
  const { darkMode, toggleDarkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleNavClick = (path) => {
    // Auto-hide sidebar after selection on desktop
    if (window.innerWidth >= 1024) {
      setSidebarCollapsed(true);
    }
    // Close mobile sidebar
    setSidebarOpen(false);
  };

  const handleSidebarMouseEnter = () => {
    if (window.innerWidth >= 1024) {
      setSidebarHover(true);
    }
  };

  const handleSidebarMouseLeave = () => {
    if (window.innerWidth >= 1024) {
      setSidebarHover(false);
    }
  };

  const isDesktopSidebarExpanded = !sidebarCollapsed || sidebarHover;

  const navigationItems = [
    { 
      path: '/', 
      label: t('navigation.ideaOfTheDay'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    { 
      path: '/dashboard', 
      label: t('navigation.dashboard'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    { 
      path: '/ideas-vault', 
      label: t('navigation.ideasVault'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    },
    { 
      path: '/builders-studio', 
      label: t('navigation.buildersStudio'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      )
    },
    { 
      path: '/profile', 
      label: t('navigation.profile'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gradient-soft-dark' : 'bg-gradient-soft-light'
    }`}>
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-50 rounded-2xl shadow-soft-xl m-4 transform transition-all duration-300 ease-soft ${
          darkMode ? 'bg-gray-800 bg-opacity-95 backdrop-blur-sm' : 'bg-white'
        } ${sidebarOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'} lg:translate-x-0 ${
          // Desktop width behavior
          isDesktopSidebarExpanded ? 'lg:w-64' : 'lg:w-16'
        }`}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center h-28 px-4 border-b ${
            darkMode ? 'border-gray-700' : 'border-gray-100'
          } ${isDesktopSidebarExpanded ? 'justify-center lg:justify-start lg:px-8' : 'justify-center'}`}>
            <Link to="/" className="flex items-center justify-center w-full" onClick={() => handleNavClick('/')}>
              <div className="flex items-center justify-center w-full max-w-[500px]">
                <img 
                  src={darkMode ? logoDarkmode : logoLightmode} 
                  alt="Idealyze Logo" 
                  className="w-full h-24 object-contain"
                />
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigationItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link-soft ${isActive(item.path) ? 'active' : ''} ${
                  darkMode ? 'text-white hover:text-indigo-400' : 'text-slate-700 hover:text-indigo-600'
                } ${
                  isDesktopSidebarExpanded ? 'lg:justify-start' : 'lg:justify-center'
                }`}
                onClick={() => handleNavClick(item.path)}
                title={!isDesktopSidebarExpanded ? item.label : ''}
              >
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-300 ${
                  isActive(item.path) 
                    ? 'bg-gradient-fuchsia text-white shadow-soft-lg' 
                    : darkMode ? 'bg-gray-700 shadow-soft-dark text-gray-300' : 'bg-white shadow-soft-md text-slate-700'
                } ${
                  isDesktopSidebarExpanded ? (isRTL ? 'ml-3' : 'mr-3') : 'lg:mr-0 lg:ml-0'
                }`}>
                  {item.icon}
                </div>
                <span className={`font-medium transition-opacity duration-300 ${
                  isActive(item.path) 
                    ? 'text-white' 
                    : darkMode ? 'text-white' : 'text-slate-700'
                } ${
                  isDesktopSidebarExpanded ? 'opacity-100' : 'lg:opacity-0 lg:w-0 lg:overflow-hidden'
                }`}>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Footer */}
          <div className={`p-4 border-t ${
            darkMode ? 'border-gray-700' : 'border-gray-100'
          }`}>
            <div className={`flex flex-col gap-2 mb-4 ${
              isDesktopSidebarExpanded ? 'items-stretch' : 'items-center'
            }`}>
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg transition-colors flex items-center justify-center ${
                  darkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>
              <LanguageToggle className={`${
                isDesktopSidebarExpanded ? 'w-full' : 'w-10 h-10 p-2'
              }`} />
            </div>
            <div className={`text-center text-sm transition-opacity duration-300 ${
              darkMode ? 'text-gray-400' : 'text-slate-500'
            } ${
              isDesktopSidebarExpanded ? 'opacity-100' : 'lg:opacity-0 lg:h-0 lg:overflow-hidden'
            }`}>
              <p className="font-semibold">{t('footer.copyright')}</p>
              <p className="text-xs mt-1">{t('app.tagline')}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className={`transition-all duration-300 ${
        isDesktopSidebarExpanded ? (isRTL ? 'lg:mr-72' : 'lg:ml-72') : (isRTL ? 'lg:mr-24' : 'lg:ml-24')
      }`}>
        {/* Top header for mobile */}
        <header className={`lg:hidden bg-opacity-95 backdrop-blur-sm shadow-soft-md sticky top-0 z-30 mx-4 mt-4 rounded-2xl ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`transition-colors p-2 rounded-lg ${
                darkMode 
                  ? 'text-gray-300 hover:text-indigo-400 hover:bg-gray-700' 
                  : 'text-slate-700 hover:text-indigo-600 hover:bg-gray-50'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
            <Link to="/" className="flex items-center justify-center">
              <div className="flex items-center justify-center w-48 h-32">
                <img 
                  src={darkMode ? logoDarkmode : logoLightmode} 
                  alt="Idealyze Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            </Link>
            <div className="w-10"></div>
          </div>
        </header>

        {/* Main content area */}
        <main className="p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;